import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowClockwise,
  DownloadSimple,
  DotsThree,
  DotsSixVertical,
  PencilSimple,
  Plus,
  Receipt,
  Trash,
  UploadSimple,
  Warning,
  Wallet,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import EventAwareDatePicker from '../../components/EventAwareDatePicker';
import AIPremiumButton from '../../components/AIPremiumGate';
import InfoTip from '../../components/InfoTip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const DEFAULT_CURRENCY = 'INR';
const MONEY_ZERO = 0.01;

const formatMoney = (amount, currency = DEFAULT_CURRENCY) => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch (_error) {
    return `${currency} ${Number(amount || 0).toLocaleString('en-IN')}`;
  }
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : 'Not set');

const getDueDateStatus = (value) => {
  if (!value) return 'none';
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return 'none';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / 86400000);
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= 2) return 'soon';
  return 'normal';
};

function DueDateText({ value, highlight = true }) {
  const status = highlight ? getDueDateStatus(value) : 'normal';
  const classes = {
    overdue: 'rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 font-medium text-rose-700',
    soon: 'rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-800',
    normal: '',
    none: '',
  };
  return <span className={classes[status] || ''}>Due {formatDate(value)}</span>;
}

const normalizeTab = (tab, canManage) => {
  if (!canManage) return 'spend';
  if (tab === 'claims' || tab === 'payments') return 'spend';
  if (tab === 'overview') return 'budget';
  return ['budget', 'spend', 'report'].includes(tab) ? tab : 'budget';
};

const getDisplayStatus = (record) => {
  const amount = Number(record?.amount || 0);
  const paid = Number(record?.paid_amount || 0);
  if (record?.status === 'needs_information') return { label: 'Needs details', tone: 'missing' };
  if (record?.status === 'awaiting_approval') return { label: 'Needs review', tone: 'wait' };
  if (record?.status === 'rejected') return { label: 'Rejected', tone: 'bad' };
  if (record?.status === 'cancelled') return { label: 'Cancelled', tone: 'muted' };
  if (record?.status === 'paid' || (amount > 0 && paid >= amount - MONEY_ZERO)) return { label: 'Paid', tone: 'good' };
  if (paid > 0) return { label: 'Part paid', tone: 'info' };
  return { label: 'Unpaid', tone: 'info' };
};

const getRecordPerson = (record) => {
  if (record?.record_type === 'team_claim') return record.claimant_name || 'Team member';
  return record?.vendor?.name || record?.claimant_name || 'Manager added';
};

const isNeedsAction = (record) => (
  record.record_type === 'team_claim'
  && ['awaiting_approval', 'needs_information'].includes(record.status)
);

function Pill({ record }) {
  const status = getDisplayStatus(record);
  const tones = {
    good: 'bg-emerald-100 text-emerald-700',
    wait: 'bg-amber-100 text-amber-800',
    info: 'bg-sky-100 text-sky-700',
    missing: 'bg-violet-100 text-violet-700',
    bad: 'bg-rose-100 text-rose-700',
    muted: 'bg-slate-100 text-slate-600',
  };
  return <Badge className={tones[status.tone] || tones.muted}>{status.label}</Badge>;
}

function Field({ label, children }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
      {options.map(([optionValue, label]) => <option key={`${optionValue}-${label}`} value={optionValue}>{label}</option>)}
    </select>
  );
}

function SummaryCard({ label, value, detail, icon: Icon, tone = 'default' }) {
  const tones = {
    default: 'bg-white',
    green: 'bg-emerald-50/70',
    gold: 'bg-amber-50/70',
    red: 'bg-rose-50/70',
  };
  return (
    <Card className={tones[tone]}>
      <CardContent className="flex items-start justify-between gap-3 p-5">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
            {detail && <InfoTip content={detail} label={`About ${label}`} />}
          </div>
          <p className="mt-2 text-2xl font-semibold text-wedding-charcoal">{value}</p>
        </div>
        <span className="rounded-lg bg-white p-2.5 text-wedding-sage shadow-sm"><Icon className="h-5 w-5" /></span>
      </CardContent>
    </Card>
  );
}

function CostCard({ record, currency, canManage, canEdit, onOpen, onApprove, onReject, onRequestDetails, onPayment, onDeclineRest }) {
  const teamSubmission = record.record_type === 'team_claim';
  const canReview = canManage && ['awaiting_approval', 'needs_information'].includes(record.status);
  const canPay = canManage && ['approved', 'scheduled'].includes(record.status) && record.outstanding_amount > MONEY_ZERO;
  const canDeclineRest = canPay && Number(record.paid_amount || 0) > MONEY_ZERO;
  return (
    <article className="rounded-xl border bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <button type="button" onClick={() => onOpen(record)} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-wedding-charcoal">{record.title || 'Untitled cost'}</h3>
            <Pill record={record} />
            {teamSubmission && <Badge className="bg-slate-100 text-slate-700">Team</Badge>}
            {record.automation?.auto_approved && <Badge className="bg-emerald-100 text-emerald-700">Auto approved</Badge>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <strong className="text-base text-foreground">{formatMoney(record.amount, currency)}</strong>
            <span>{getRecordPerson(record)}</span>
            {record.category?.name && <Badge variant="outline">{record.category.name}</Badge>}
            {record.outstanding_amount > MONEY_ZERO && (
              <Badge className="bg-amber-100 text-amber-800">{formatMoney(record.outstanding_amount, currency)} due</Badge>
            )}
            {record.due_date && <DueDateText value={record.due_date} highlight={record.outstanding_amount > MONEY_ZERO} />}
          </div>
        </button>
        <div className="flex items-center gap-2 md:justify-end">
          {canReview && record.status === 'awaiting_approval' && (
            <Button size="sm" onClick={() => onApprove(record)}>{teamSubmission ? 'Approve claim' : 'Approve'}</Button>
          )}
          {canPay && <Button size="sm" onClick={() => onPayment(record)}>Record payment</Button>}
          {!canPay && !(canReview && record.status === 'awaiting_approval') && (
            <Button size="sm" variant="outline" onClick={() => onOpen(record)}>
              {canEdit ? 'Edit' : 'View'}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="More cost actions">
                <DotsThree style={{ width: 24, height: 24 }} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onOpen(record)}>View details</DropdownMenuItem>
              {record.document_url && (
                <DropdownMenuItem asChild>
                  <a href={resolveMediaUrl(record.document_url)} target="_blank" rel="noreferrer"><Receipt className="h-4 w-4" /> Receipt</a>
                </DropdownMenuItem>
              )}
              {canReview && record.status === 'awaiting_approval' && (
                <>
                  <DropdownMenuItem onSelect={() => onRequestDetails(record)}>Request details</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onReject(record)} className="text-red-600">Reject</DropdownMenuItem>
                </>
              )}
              {canDeclineRest && <DropdownMenuItem onSelect={() => onDeclineRest(record)}>Decline remaining</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </article>
  );
}

function CategoryRow({ category, currency, onEdit, onDragStart, onDragEnd, onDrop, isDragging }) {
  const utilization = Math.min(100, category.utilization_percent || 0);
  const over = (category.utilization_percent || 0) > 100;
  return (
    <div
      className={`rounded-xl border bg-white p-4 transition-opacity ${isDragging ? 'opacity-50' : ''}`}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(category);
      }}
    >
      <div className="flex items-center justify-between gap-3 text-sm">
        <div className="flex min-w-0 items-start gap-2">
          <span
            draggable
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move';
              onDragStart(category);
            }}
            onDragEnd={onDragEnd}
            className="mt-0.5 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
            title="Drag to reorder"
            aria-label={`Drag ${category.name} to reorder`}
          >
            <DotsSixVertical className="h-5 w-5" />
          </span>
          <div className="min-w-0">
          <div className="flex items-center gap-1">
            <p className="font-semibold">{category.name}</p>
            <InfoTip
              content={`Team auto-approve: ${category.team_auto_approve_limit ? formatMoney(category.team_auto_approve_limit, currency) : 'approval required'}.`}
              label={`About ${category.name} approvals`}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {formatMoney(category.forecast_amount, currency)} / {formatMoney(category.approved_amount, currency)}
          </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {over && <Badge className="bg-rose-100 text-rose-700">Over</Badge>}
          <Button size="sm" variant="outline" onClick={() => onEdit(category)}>Edit</Button>
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${over ? 'bg-rose-500' : utilization > 80 ? 'bg-amber-500' : 'bg-wedding-sage'}`} style={{ width: `${utilization}%` }} />
      </div>
    </div>
  );
}

function CategoryEditor({ category, currency, busy, onSave, onDelete }) {
  const [values, setValues] = useState({
    name: category?.name || '',
    planned_amount: category?.planned_amount || '',
    approved_amount: category?.approved_amount || '',
    team_auto_approve_limit: category?.team_auto_approve_limit ?? '',
  });

  useEffect(() => {
    setValues({
      name: category?.name || '',
      planned_amount: category?.planned_amount || '',
      approved_amount: category?.approved_amount || '',
      team_auto_approve_limit: category?.team_auto_approve_limit ?? '',
    });
  }, [category]);

  if (!category) return null;

  const payload = {
    name: values.name,
    planned_amount: Number(values.planned_amount || 0),
    approved_amount: Number(values.approved_amount || 0),
    team_auto_approve_limit: values.team_auto_approve_limit === '' ? null : Number(values.team_auto_approve_limit),
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
        Forecast: {formatMoney(category.forecast_amount, currency)}. Team claims over the category auto-approval limit stay in Needs review.
      </div>
      <Field label="Category name"><Input value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Planned amount"><Input type="number" min="0" value={values.planned_amount} onChange={(event) => setValues({ ...values, planned_amount: event.target.value })} /></Field>
        <Field label="Budget limit"><Input type="number" min="0" value={values.approved_amount} onChange={(event) => setValues({ ...values, approved_amount: event.target.value })} /></Field>
      </div>
      <Field label="Auto-approve team claims up to">
        <Input type="number" min="0" placeholder="Leave blank to require approval" value={values.team_auto_approve_limit} onChange={(event) => setValues({ ...values, team_auto_approve_limit: event.target.value })} />
      </Field>
      <DialogFooter className="gap-2 sm:gap-2">
        <Button type="button" variant="outline" onClick={() => onDelete(category)} disabled={busy}>
          <Trash className="mr-2 h-4 w-4" /> Remove
        </Button>
        <Button type="button" onClick={() => onSave(category, payload)} disabled={busy}>Save category</Button>
      </DialogFooter>
    </div>
  );
}

export default function BudgetManagement() {
  const { currentEvent, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [context, setContext] = useState(null);
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkExpenseOpen, setBulkExpenseOpen] = useState(false);
  const [downloadingExpenseTemplate, setDownloadingExpenseTemplate] = useState(false);
  const [uploadingExpenses, setUploadingExpenses] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryEditing, setCategoryEditing] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [detailDraft, setDetailDraft] = useState({ title: '', amount: '', category_id: '', description: '' });
  const [requestNote, setRequestNote] = useState('');

  const canManage = Boolean(context?.can_manage);
  const activeTab = normalizeTab(searchParams.get('tab'), canManage);
  const currency = overview?.currency || overview?.plan?.currency || DEFAULT_CURRENCY;
  const autoApprovesOwnSubmission = canManage && user?.role === 'event_manager';
  const currentUserId = user?.id || user?.sub;

  const [setup, setSetup] = useState({
    total_budget: '',
    currency: DEFAULT_CURRENCY,
    guest_count: '',
    contingency_percent: '5',
    auto_approve_limit: '5000',
  });

  const [cost, setCost] = useState({
    source: 'manager',
    file: null,
    title: '',
    amount: '',
    merchant: '',
    description: '',
    category_id: '',
    vendor_id: '',
    itinerary_item_id: '',
    invoice_number: '',
    expense_date: '',
    due_date: '',
    record_type: 'manual_expense',
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    planned_amount: '',
    approved_amount: '',
    team_auto_approve_limit: '',
  });

  const [payment, setPayment] = useState({ amount: '', paid_at: '', method: '', reference: '', note: '' });

  const loadData = useCallback(async () => {
    if (!currentEvent?.id) return;
    setLoading(true);
    try {
      const contextResponse = await api.get(`/events/${currentEvent.id}/budget/context`);
      setContext(contextResponse.data);
      const recordsRequest = api.get(`/events/${currentEvent.id}/budget/records`);
      const paymentsRequest = api.get(`/events/${currentEvent.id}/budget/payments`);
      if (contextResponse.data.can_manage) {
        const [overviewResponse, recordsResponse, paymentsResponse] = await Promise.all([
          api.get(`/events/${currentEvent.id}/budget/overview`),
          recordsRequest,
          paymentsRequest,
        ]);
        setOverview(overviewResponse.data);
        setRecords(recordsResponse.data || []);
        setPayments(paymentsResponse.data || []);
        if (overviewResponse.data.configured) {
          setSetup((current) => ({
            ...current,
            total_budget: overviewResponse.data.plan.total_budget,
            currency: overviewResponse.data.currency,
            guest_count: overviewResponse.data.plan.guest_count || '',
            contingency_percent: overviewResponse.data.plan.contingency_percent,
            auto_approve_limit: overviewResponse.data.plan.auto_approve_limit,
          }));
        } else {
          setSetupOpen(true);
        }
      } else {
        const [recordsResponse, paymentsResponse] = await Promise.all([recordsRequest, paymentsRequest]);
        setRecords(recordsResponse.data || []);
        setPayments(paymentsResponse.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the budget workspace');
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!canManage && activeTab !== 'spend') setSearchParams({ tab: 'spend' });
  }, [activeTab, canManage, setSearchParams]);

  const attentionRecords = useMemo(() => records.filter(isNeedsAction), [records]);
  const spendRecords = useMemo(
    () => records.filter((record) => ['approved', 'scheduled', 'paid'].includes(record.status)),
    [records],
  );

  const recordsById = useMemo(() => (
    records.reduce((lookup, record) => ({ ...lookup, [record.id]: record }), {})
  ), [records]);

  const filteredPayments = useMemo(() => (
    payments.filter((paymentItem) => {
      const record = recordsById[paymentItem.record_id];
      if (paymentFilter === 'team') return record?.record_type === 'team_claim';
      if (paymentFilter === 'manager') return record && record.record_type !== 'team_claim';
      return true;
    })
  ), [paymentFilter, payments, recordsById]);

  const totalBudget = Number(overview?.total_budget ?? overview?.plan?.total_budget ?? 0);
  const spentTillDate = Number(overview?.forecast || 0);
  const remainingBudget = Number(overview?.available ?? totalBudget - spentTillDate);

  const changeTab = (value) => {
    setSearchParams({ tab: value });
  };

  const openBudgetSetup = () => {
    const plan = overview?.plan;
    if (plan) {
      setSetup({
        total_budget: plan.total_budget ?? '',
        currency: overview?.currency || plan.currency || DEFAULT_CURRENCY,
        guest_count: plan.guest_count ?? '',
        contingency_percent: plan.contingency_percent ?? '5',
        auto_approve_limit: plan.auto_approve_limit ?? '5000',
      });
    }
    setSetupOpen(true);
  };

  const submitSetup = async (event) => {
    event.preventDefault();
    try {
      setBusy('setup');
      await api.post(`/events/${currentEvent.id}/budget/setup`, {
        ...setup,
        total_budget: Number(setup.total_budget),
        guest_count: Number(setup.guest_count || 0),
        contingency_percent: Number(setup.contingency_percent),
        auto_approve_limit: Number(setup.auto_approve_limit),
      });
      toast.success(overview?.configured ? 'Budget updated' : 'Budget plan generated');
      setSetupOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the budget');
    } finally {
      setBusy('');
    }
  };

  const resetCost = () => {
    setCost({
      source: canManage ? 'manager' : 'team',
      file: null,
      title: '',
      amount: '',
      merchant: '',
      description: '',
      category_id: '',
      vendor_id: '',
      itinerary_item_id: '',
      invoice_number: '',
      expense_date: '',
      due_date: '',
      record_type: 'manual_expense',
    });
  };

  const openAddCost = () => {
    resetCost();
    setAddOpen(true);
  };

  const openDetail = (record) => {
    setDetailRecord(record);
    setRequestNote('');
    setDetailDraft({
      title: record.title || '',
      amount: record.amount || '',
      category_id: record.category_id || '',
      description: record.description || '',
    });
  };

  const submitCost = async (event) => {
    event.preventDefault();
    const isDocumentSubmission = Boolean(cost.file);
    if (!canManage && (!cost.title.trim() || Number(cost.amount) <= 0 || !cost.expense_date)) {
      toast.error('Enter an expense name, amount, and expense date');
      return;
    }
    if (canManage && !isDocumentSubmission && (!cost.title || !cost.amount)) {
      toast.error('Enter a title and amount');
      return;
    }

    try {
      setBusy('cost');
      if (isDocumentSubmission || !canManage) {
        const formData = new FormData();
        if (cost.file) formData.append('file', cost.file);
        formData.append('claim_type', cost.source === 'team' ? 'reimbursement' : 'vendor_invoice');
        if (cost.amount !== '') formData.append('amount', cost.amount);
        if (cost.title || cost.merchant) formData.append('merchant', canManage ? cost.merchant || cost.title : cost.title);
        if (cost.description) formData.append('description', cost.description);
        if (cost.category_id) formData.append('category_id', cost.category_id);
        if (cost.vendor_id) formData.append('vendor_id', cost.vendor_id);
        if (cost.itinerary_item_id) formData.append('itinerary_item_id', cost.itinerary_item_id);
        if (cost.invoice_number) formData.append('invoice_number', cost.invoice_number);
        if (cost.expense_date) formData.append('expense_date', cost.expense_date);
        if (cost.due_date) formData.append('due_date', cost.due_date);
        const response = await api.post(`/events/${currentEvent.id}/budget/claims`, formData);
        const automation = response.data.automation || {};
        if (response.data.status === 'needs_information') {
          toast.info(isDocumentSubmission
            ? 'Receipt saved. Missing details can be completed later.'
            : 'Expense saved. Missing details can be completed later.');
        } else {
          toast.success(automation.auto_approved ? 'Cost added and approved' : 'Cost submitted for review');
        }
        if (automation.duplicate_record_id) toast.info('A possible duplicate was detected and held for review');
      } else {
        await api.post(`/events/${currentEvent.id}/budget/records`, {
          record_type: cost.record_type,
          title: cost.title,
          amount: Number(cost.amount),
          category_id: cost.category_id || null,
          vendor_id: cost.vendor_id || null,
          itinerary_item_id: cost.itinerary_item_id || null,
          due_date: cost.due_date ? new Date(cost.due_date).toISOString() : null,
          description: cost.description || null,
        });
        toast.success('Cost added and approved');
      }
      setAddOpen(false);
      resetCost();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add the cost');
    } finally {
      setBusy('');
    }
  };

  const downloadExpenseTemplate = async () => {
    if (!currentEvent?.id) return;
    try {
      setDownloadingExpenseTemplate(true);
      const response = await api.get(
        `/events/${currentEvent.id}/budget/claims/bulk-template`,
        { responseType: 'blob' },
      );
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1]
        || `expense_bulk_template_${currentEvent.id}.xlsx`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Expense template downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not download the expense template');
    } finally {
      setDownloadingExpenseTemplate(false);
    }
  };

  const uploadExpenseWorkbook = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentEvent?.id) return;
    try {
      setUploadingExpenses(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/events/${currentEvent.id}/budget/claims/bulk`,
        formData,
      );
      const added = response.data?.expenses_added || 0;
      const skipped = response.data?.skipped || [];
      const skippedSummary = skipped
        .slice(0, 4)
        .map((item) => `Row ${item.row}: ${item.reason}`)
        .join('\n');
      if (added === 0) {
        toast.error('No expenses were added', { description: skippedSummary || undefined });
      } else if (skipped.length > 0) {
        toast.warning(response.data.message, { description: skippedSummary });
      } else {
        toast.success(response.data.message);
      }
      if (added > 0) {
        setBulkExpenseOpen(false);
        await loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not upload expenses');
    } finally {
      setUploadingExpenses(false);
      event.target.value = '';
    }
  };

  const updateStatus = async (record, status, note = '') => {
    try {
      setBusy(record.id);
      await api.put(`/events/${currentEvent.id}/budget/records/${record.id}/status`, { status, note: note || null });
      toast.success(status === 'approved' ? 'Cost approved' : status === 'needs_information' ? 'Sent back for details' : 'Cost updated');
      setDetailRecord(null);
      setRequestNote('');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update approval');
    } finally {
      setBusy('');
    }
  };

  const requestDetails = async (record, note = '') => {
    await updateStatus(record, 'needs_information', note || 'Please add the missing details and resend for approval.');
  };

  const canEditDetails = (record) => {
    if (!record) return false;
    if (!canManage) {
      return record.status === 'needs_information' && record.claimant_user_id === currentUserId;
    }
    const hasRecordedPayment = Number(record.paid_amount || 0) > 0 || payments.some((item) => item.record_id === record.id);
    return record.created_by_user_id === currentUserId && !hasRecordedPayment;
  };

  const saveRecordDetails = async ({ submitForApproval = false } = {}) => {
    if (!detailRecord) return;
    try {
      setBusy('details');
      const response = await api.put(`/events/${currentEvent.id}/budget/records/${detailRecord.id}`, {
        title: detailDraft.title,
        amount: Number(detailDraft.amount || 0),
        category_id: detailDraft.category_id || null,
        description: detailDraft.description,
        submit_for_approval: submitForApproval,
      });
      toast.success(submitForApproval ? 'Sent back for approval' : 'Cost details saved');
      setDetailRecord(response.data);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save details');
    } finally {
      setBusy('');
    }
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    try {
      setBusy('payment');
      await api.post(`/events/${currentEvent.id}/budget/records/${paymentRecord.id}/payments`, {
        ...payment,
        amount: Number(payment.amount),
        paid_at: payment.paid_at ? new Date(payment.paid_at).toISOString() : new Date().toISOString(),
      });
      toast.success('Payment recorded under Spend');
      setPaymentRecord(null);
      setPayment({ amount: '', paid_at: '', method: '', reference: '', note: '' });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record the payment');
    } finally {
      setBusy('');
    }
  };

  const declineRemaining = async (record) => {
    try {
      setBusy(record.id);
      await api.put(`/events/${currentEvent.id}/budget/records/${record.id}/decline-remainder`);
      toast.success('Remaining balance declined');
      setDetailRecord(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not decline the remaining balance');
    } finally {
      setBusy('');
    }
  };

  const saveCategory = async (category, values) => {
    try {
      setBusy(category.id);
      await api.put(`/events/${currentEvent.id}/budget/categories/${category.id}`, values);
      toast.success(`${category.name} updated`);
      setCategoryEditing(null);
      setCategoryDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the category');
    } finally {
      setBusy('');
    }
  };

  const reorderCategories = async (targetCategory) => {
    if (!draggedCategoryId || draggedCategoryId === targetCategory.id) {
      setDraggedCategoryId('');
      return;
    }
    const currentCategories = overview?.categories || [];
    const sourceIndex = currentCategories.findIndex((category) => category.id === draggedCategoryId);
    const targetIndex = currentCategories.findIndex((category) => category.id === targetCategory.id);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggedCategoryId('');
      return;
    }

    const reordered = [...currentCategories];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setOverview((current) => ({
      ...current,
      categories: reordered.map((category, index) => ({ ...category, sort_order: index })),
    }));
    setDraggedCategoryId('');
    try {
      await api.put(`/events/${currentEvent.id}/budget/categories/order`, {
        category_ids: reordered.map((category) => category.id),
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reorder categories');
      await loadData();
    }
  };

  const createCategory = async () => {
    if (!newCategory.name) {
      toast.error('Enter a category name');
      return;
    }
    try {
      setBusy('category-create');
      await api.post(`/events/${currentEvent.id}/budget/categories`, {
        name: newCategory.name,
        planned_amount: Number(newCategory.planned_amount || 0),
        approved_amount: Number(newCategory.approved_amount || 0),
        team_auto_approve_limit: newCategory.team_auto_approve_limit === '' ? null : Number(newCategory.team_auto_approve_limit),
      });
      toast.success('Category added');
      setNewCategory({ name: '', planned_amount: '', approved_amount: '', team_auto_approve_limit: '' });
      setCategoryDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add category');
    } finally {
      setBusy('');
    }
  };

  const deleteCategory = async (category) => {
    try {
      setBusy(category.id);
      await api.delete(`/events/${currentEvent.id}/budget/categories/${category.id}`);
      toast.success(`${category.name} removed`);
      setCategoryEditing(null);
      setCategoryDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove category');
    } finally {
      setBusy('');
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get(`/events/${currentEvent.id}/budget/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${currentEvent.name || 'event'}-budget.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Could not download the budget report');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="h-96 animate-pulse rounded-2xl bg-muted" /></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StickyActionHeader>
          <div className="flex flex-wrap items-center gap-1">
            <h1 className="font-serif text-3xl font-bold text-wedding-charcoal">{canManage ? 'Budget' : 'My Expenses'}</h1>
            <InfoTip
              content={canManage ? 'Track budget, costs, team reimbursements, payments, and client reporting.' : 'Submit expenses and track approval and payment status.'}
              label={canManage ? 'About budget management' : 'About my expenses'}
            />
            {canManage && (
              <span className="ml-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                This budget is only visible to you
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2" data-header-action-group>
            <Button variant="outline" onClick={loadData} aria-label="Refresh budget data" title="Refresh budget data" data-header-action>
              <ArrowClockwise className="mr-2 h-4 w-4" /> Refresh
            </Button>
            {canManage && overview?.configured && (
              <Button variant="outline" onClick={openBudgetSetup} data-header-action>
                <PencilSimple className="mr-2 h-4 w-4" /> Edit Budget
              </Button>
            )}
            <Button variant="outline" onClick={() => setBulkExpenseOpen(true)} data-header-action>
              <UploadSimple className="mr-2 h-4 w-4" /> Import Expenses
            </Button>
            <Button onClick={openAddCost} data-header-action>
              <Plus className="mr-2 h-4 w-4" /> {canManage ? 'Add Cost' : 'Submit Expense'}
            </Button>
          </div>
        </StickyActionHeader>

        <Tabs value={activeTab} onValueChange={changeTab}>
          <TabsList className={`grid h-auto w-full ${canManage ? 'grid-cols-3' : 'grid-cols-1'}`}>
            {canManage && <TabsTrigger value="budget">Budget</TabsTrigger>}
            <TabsTrigger value="spend">{canManage ? 'Spend' : 'My Expenses'}</TabsTrigger>
            {canManage && <TabsTrigger value="report">Report</TabsTrigger>}
          </TabsList>

          {canManage && (
            <TabsContent value="budget" className="space-y-6">
              {!overview?.configured ? (
                <Card className="border-wedding-gold/40 bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="py-12 text-center">
                    <Wallet className="mx-auto h-12 w-12 text-wedding-sage" />
                    <div className="mt-4 flex items-center justify-center gap-1">
                      <h2 className="font-serif text-2xl">Create the event budget</h2>
                      <InfoTip content="Set the total once. GalaGo creates default categories that you can edit, remove, or extend." label="About budget setup" />
                    </div>
                    <Button className="mt-5" onClick={openBudgetSetup}>Start budget setup</Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <SummaryCard label="Total budget" value={formatMoney(totalBudget, currency)} detail="Current event budget" icon={Wallet} />
                    <SummaryCard label="Spent till date" value={formatMoney(spentTillDate, currency)} detail="Approved costs and commitments" icon={Receipt} tone="green" />
                    <SummaryCard label="Remaining" value={`${formatMoney(remainingBudget, currency)} / ${formatMoney(totalBudget, currency)}`} detail="Remaining / total budget" icon={remainingBudget < 0 ? Warning : Wallet} tone={remainingBudget < 0 ? 'red' : 'default'} />
                  </div>

                  <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
                    <Card>
                      <CardHeader className="flex-row items-center justify-between gap-3">
                        <div className="flex items-center gap-1">
                          <CardTitle className="font-serif">Needs attention</CardTitle>
                          <InfoTip content="Only team expenses awaiting review or missing details appear here." label="About items needing attention" />
                        </div>
                        <Button variant="outline" onClick={() => changeTab('spend')}>Review spend</Button>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {attentionRecords.slice(0, 4).map((record) => (
                          <CostCard key={record.id} record={record} currency={currency} canManage={canManage} canEdit={canEditDetails(record)} onOpen={openDetail} onApprove={(item) => updateStatus(item, 'approved')} onReject={(item) => updateStatus(item, 'rejected')} onRequestDetails={requestDetails} onPayment={setPaymentRecord} onDeclineRest={declineRemaining} />
                        ))}
                        {!attentionRecords.length && <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">Nothing needs attention right now.</p>}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex-row items-center justify-between gap-3">
                        <div className="flex items-center gap-1">
                          <CardTitle className="font-serif">Categories</CardTitle>
                          <InfoTip content="Budget limits and team auto-approval rules." label="About budget categories" />
                        </div>
                        <Button variant="outline" onClick={() => { setCategoryEditing(null); setCategoryDialogOpen(true); }}>Add</Button>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className={`rounded-lg border px-3 py-2 text-sm ${Number(overview.uncategorized_budget || 0) < 0 ? 'border-rose-200 bg-rose-50 text-rose-700' : 'bg-muted/30 text-muted-foreground'}`}>
                          {Number(overview.uncategorized_budget || 0) < 0
                            ? `Categories exceed the total budget by ${formatMoney(Math.abs(overview.uncategorized_budget), currency)}`
                            : `${formatMoney(overview.uncategorized_budget, currency)} of the total budget is not assigned to a category`}
                        </div>
                        {(overview.categories || []).map((category) => (
                          <CategoryRow
                            key={category.id}
                            category={category}
                            currency={currency}
                            onEdit={(item) => { setCategoryEditing(item); setCategoryDialogOpen(true); }}
                            onDragStart={(item) => setDraggedCategoryId(item.id)}
                            onDragEnd={() => setDraggedCategoryId('')}
                            onDrop={reorderCategories}
                            isDragging={draggedCategoryId === category.id}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </TabsContent>
          )}

          <TabsContent value="spend" className="space-y-5">
            {!canManage && (
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-1">
                    <CardTitle className="font-serif">My submitted expenses</CardTitle>
                    <InfoTip content="Returned claims can be opened, completed, and resent for approval." label="About submitted expenses" />
                  </div>
                  <Button onClick={openAddCost}><Plus className="mr-2 h-4 w-4" /> Submit Expense</Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {records.map((record) => (
                    <CostCard key={record.id} record={record} currency={currency} canManage={false} canEdit={canEditDetails(record)} onOpen={openDetail} onApprove={() => {}} onReject={() => {}} onRequestDetails={() => {}} onPayment={() => {}} onDeclineRest={() => {}} />
                  ))}
                  {!records.length && <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">No expenses submitted yet.</p>}
                </CardContent>
              </Card>
            )}
            {canManage && (
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-1">
                    <CardTitle className="font-serif">Spend</CardTitle>
                    <InfoTip content="Manager-added costs appear here immediately. Team expenses appear after approval." label="About approved spend" />
                  </div>
                  <Button onClick={openAddCost}><Plus className="mr-2 h-4 w-4" /> Add Cost</Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {spendRecords.map((record) => (
                    <CostCard key={record.id} record={record} currency={currency} canManage={canManage} canEdit={canEditDetails(record)} onOpen={openDetail} onApprove={(item) => updateStatus(item, 'approved')} onReject={(item) => updateStatus(item, 'rejected')} onRequestDetails={requestDetails} onPayment={setPaymentRecord} onDeclineRest={declineRemaining} />
                  ))}
                  {!spendRecords.length && <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">No approved costs yet.</p>}
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-1">
                  <CardTitle className="font-serif">{canManage ? 'Payments' : 'My Payments'}</CardTitle>
                  <InfoTip content="Payments recorded against approved costs appear here." label="About recorded payments" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {[
                    ['all', 'All payments'],
                    ['team', 'Team'],
                    ['manager', canManage ? 'Manager added' : 'Myself'],
                  ].filter(([value]) => canManage || value !== 'team').map(([value, label]) => (
                    <Button key={value} type="button" size="sm" variant={paymentFilter === value ? 'default' : 'outline'} onClick={() => setPaymentFilter(value)}>{label}</Button>
                  ))}
                </div>
                <div className="space-y-3">
                  {filteredPayments.map((paymentItem) => {
                    const record = recordsById[paymentItem.record_id];
                    return (
                      <article key={paymentItem.id} className="rounded-xl border bg-white p-4 shadow-sm">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-semibold text-wedding-charcoal">{record?.title || 'Recorded payment'}</h3>
                              <Badge className={record?.record_type === 'team_claim' ? 'bg-slate-100 text-slate-700' : 'bg-sky-100 text-sky-700'}>
                                {record?.record_type === 'team_claim' ? 'Team' : 'Manager added'}
                              </Badge>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {record ? getRecordPerson(record) : 'Cost record'} - {paymentItem.method || 'Payment'} - {formatDate(paymentItem.paid_at)}
                            </p>
                            {paymentItem.reference && <p className="mt-1 text-xs text-muted-foreground">Reference: {paymentItem.reference}</p>}
                          </div>
                          <div className="flex items-center gap-2 md:text-right">
                            <strong>{formatMoney(paymentItem.amount, currency)}</strong>
                            {record && <Button size="sm" variant="outline" onClick={() => openDetail(record)}>View cost</Button>}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                  {!filteredPayments.length && <p className="rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">No recorded payments match this filter.</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {canManage && (
            <TabsContent value="report" className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-3">
                <SummaryCard label="Total budget" value={formatMoney(totalBudget, currency)} detail="Current event budget" icon={Wallet} />
                <SummaryCard label="Spent till date" value={formatMoney(spentTillDate, currency)} detail="Approved costs and commitments" icon={Receipt} tone="green" />
                <SummaryCard label="Remaining" value={`${formatMoney(remainingBudget, currency)} / ${formatMoney(totalBudget, currency)}`} detail="Remaining / total budget" icon={remainingBudget < 0 ? Warning : Wallet} tone={remainingBudget < 0 ? 'red' : 'default'} />
              </div>

              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-1">
                    <CardTitle className="font-serif">Client report</CardTitle>
                    <InfoTip content="Estimate versus actual, with a category over or under summary." label="About the client report" />
                  </div>
                  <Button onClick={downloadReport}><DownloadSimple className="mr-2 h-4 w-4" /> Export CSV</Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(overview?.categories || []).map((category) => (
                    <div key={category.id} className="grid gap-2 rounded-xl border p-4 text-sm md:grid-cols-[1fr_auto_auto] md:items-center">
                      <span className="font-medium">{category.name}</span>
                      <span className="text-muted-foreground">Estimated {formatMoney(category.approved_amount, currency)}</span>
                      <span className={(category.available_amount || 0) < 0 ? 'font-medium text-rose-600' : 'font-medium text-emerald-700'}>
                        {(category.available_amount || 0) < 0 ? `${formatMoney(Math.abs(category.available_amount), currency)} over` : `${formatMoney(category.available_amount, currency)} left`}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-2xl">
          <form onSubmit={submitSetup}>
            <DialogHeader>
              <DialogTitle>{overview?.configured ? 'Edit budget' : 'Budget setup'}</DialogTitle>
              <DialogDescription>
                {overview?.configured
                  ? 'Update the event budget settings. Existing costs, payments, and categories are preserved.'
                  : 'Set the baseline. Default categories are generated only if none exist.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-5 sm:grid-cols-2">
              <Field label="Total event budget"><Input type="number" min="1" required value={setup.total_budget} onChange={(event) => setSetup({ ...setup, total_budget: event.target.value })} /></Field>
              <Field label="Currency"><Input maxLength={3} required value={setup.currency} onChange={(event) => setSetup({ ...setup, currency: event.target.value.toUpperCase() })} /></Field>
              <Field label="Expected guests"><Input type="number" min="0" value={setup.guest_count} onChange={(event) => setSetup({ ...setup, guest_count: event.target.value })} /></Field>
              <Field label="Contingency %"><Input type="number" min="0" max="30" value={setup.contingency_percent} onChange={(event) => setSetup({ ...setup, contingency_percent: event.target.value })} /></Field>
              <Field label="Default auto-approve up to"><Input type="number" min="0" value={setup.auto_approve_limit} onChange={(event) => setSetup({ ...setup, auto_approve_limit: event.target.value })} /></Field>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button><Button type="submit" disabled={busy === 'setup'}>{busy === 'setup' ? 'Saving...' : overview?.configured ? 'Update budget' : 'Save budget'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <form onSubmit={submitCost}>
            <DialogHeader>
              <DialogTitle>{canManage ? 'Add Cost' : 'Submit Expense'}</DialogTitle>
              <DialogDescription>
                {canManage ? 'Manager-added costs are approved immediately. Uploading a document keeps AI extraction, matching, and duplicate checks active.' : 'Team expenses go to manager review unless category rules auto-approve them.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-5 sm:grid-cols-2">
              {!canManage && (
                <Field label="Expense name">
                  <Input required value={cost.title} onChange={(event) => setCost({ ...cost, title: event.target.value })} />
                </Field>
              )}
              {canManage && (
                <>
                  <Field label="Cost type"><Select value={cost.record_type} onChange={(value) => setCost({ ...cost, record_type: value })} options={[['manual_expense', 'Cost'], ['vendor_invoice', 'Vendor bill'], ['commitment', 'Vendor commitment']]} /></Field>
                  <Field label="Submission"><Select value={cost.source} onChange={(value) => setCost({ ...cost, source: value })} options={[['manager', 'Manager cost'], ['team', 'Team reimbursement']]} /></Field>
                </>
              )}
              <Field label="Amount"><Input required={!canManage} type="number" min={canManage ? '0' : '0.01'} step="0.01" value={cost.amount} onChange={(event) => setCost({ ...cost, amount: event.target.value })} placeholder={canManage ? 'Optional if receipt can be read' : ''} /></Field>
              {!canManage && (
                <Field label="Expense date">
                  <Input required type="date" value={cost.expense_date} onChange={(event) => setCost({ ...cost, expense_date: event.target.value })} />
                </Field>
              )}
              <Field label={canManage ? 'Receipt or invoice' : 'Receipt (optional)'}><Input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setCost({ ...cost, file: event.target.files?.[0] || null })} /></Field>
              <div className="flex flex-col gap-3 rounded-lg border border-violet-200 bg-violet-50/60 p-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-violet-900">{canManage ? 'AI features remain active: receipt reading, vendor matching, category suggestion, duplicate checks, and fallback to Needs details.' : 'Upload a receipt to keep auto-fill, matching, and duplicate checks available.'}</p>
                <AIPremiumButton featureName="AI invoice auto-fill" variant="outline" className="shrink-0 bg-white">
                  Auto-fill details
                </AIPremiumButton>
              </div>
              {canManage && (
                <>
                  <Field label="What is it for?"><Input value={cost.title} onChange={(event) => setCost({ ...cost, title: event.target.value })} /></Field>
                  <Field label="Merchant or vendor"><Input value={cost.merchant} onChange={(event) => setCost({ ...cost, merchant: event.target.value })} /></Field>
                  <Field label="Category"><Select value={cost.category_id} onChange={(value) => setCost({ ...cost, category_id: value })} options={[['', 'Suggest automatically'], ...(context?.categories || []).map((item) => [item.id, item.name])]} /></Field>
                  <Field label="Existing vendor"><Select value={cost.vendor_id} onChange={(value) => setCost({ ...cost, vendor_id: value })} options={[['', 'Match automatically'], ...(context?.vendors || []).map((item) => [item.id, item.name])]} /></Field>
                </>
              )}
              <Field label={canManage ? 'Function' : 'Event name'}><Select value={cost.itinerary_item_id} onChange={(value) => setCost({ ...cost, itinerary_item_id: value })} options={[['', canManage ? 'Optional' : 'Select an event (optional)'], ...(context?.itinerary || []).map((item) => [item.id, item.name])]} /></Field>
              {canManage && (
                <>
                  <Field label="Due date">
                    <EventAwareDatePicker value={cost.due_date} onChange={(date) => setCost({ ...cost, due_date: date })} eventStartDate={currentEvent?.start_date} eventEndDate={currentEvent?.end_date} defaultDate={currentEvent?.end_date} />
                  </Field>
                  <Field label="Invoice number"><Input value={cost.invoice_number} onChange={(event) => setCost({ ...cost, invoice_number: event.target.value })} /></Field>
                </>
              )}
              <div className="sm:col-span-2"><Field label="Notes"><Textarea value={cost.description} onChange={(event) => setCost({ ...cost, description: event.target.value })} /></Field></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button type="submit" disabled={busy === 'cost'}>{busy === 'cost' ? 'Saving...' : canManage ? 'Save cost' : 'Submit expense'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkExpenseOpen} onOpenChange={setBulkExpenseOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import expenses</DialogTitle>
            <DialogDescription>
              Download the template, add one expense per row, then upload the completed Excel file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2 rounded-lg border p-4">
              <p className="text-sm font-medium">Step 1: Download template</p>
              <p className="text-sm text-muted-foreground">
                Expense name, amount, and expense date are required. Event name and notes are optional.
              </p>
              <Button type="button" variant="outline" onClick={downloadExpenseTemplate} disabled={downloadingExpenseTemplate}>
                <DownloadSimple className="mr-2 h-4 w-4" />
                {downloadingExpenseTemplate ? 'Downloading...' : 'Download Excel template'}
              </Button>
            </div>
            <div className="space-y-2 rounded-lg border p-4">
              <p className="text-sm font-medium">Step 2: Upload completed template</p>
              <Input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={uploadExpenseWorkbook}
                disabled={uploadingExpenses}
              />
              {uploadingExpenses && <p className="text-sm text-muted-foreground">Uploading expenses...</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkExpenseOpen(false)} disabled={uploadingExpenses}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailRecord)} onOpenChange={(open) => {
        if (!open) {
          setDetailRecord(null);
          setRequestNote('');
        }
      }}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailRecord?.title || 'Cost details'}</DialogTitle>
            <DialogDescription>{detailRecord?.record_type === 'team_claim' ? 'Team claim details for manager review.' : 'Cost details, document, automation, and payment state.'}</DialogDescription>
          </DialogHeader>
          {detailRecord && (
            <div className="space-y-4 py-2">
              <div className="grid gap-3 sm:grid-cols-2">
                <Detail label="Amount" value={formatMoney(detailRecord.amount, currency)} />
                <Detail label="Paid" value={`${formatMoney(detailRecord.paid_amount, currency)} paid`} />
                <Detail label={detailRecord.record_type === 'team_claim' ? 'Claimant' : 'Vendor/person'} value={getRecordPerson(detailRecord)} />
                <Detail label="Category" value={detailRecord.category?.name || 'Uncategorized'} />
                <Detail label="Status" value={getDisplayStatus(detailRecord).label} />
                <Detail label="Due date" value={detailRecord.due_date ? <DueDateText value={detailRecord.due_date} highlight={detailRecord.outstanding_amount > MONEY_ZERO} /> : 'Not set'} />
              </div>
              {detailRecord.document_url && (
                <Button asChild variant="outline">
                  <a href={resolveMediaUrl(detailRecord.document_url)} target="_blank" rel="noreferrer"><Receipt className="mr-2 h-4 w-4" /> View receipt or invoice</a>
                </Button>
              )}
              {detailRecord.automation?.auto_approved && <Badge className="bg-emerald-100 text-emerald-700">Auto approved</Badge>}
              {detailRecord.extraction && Object.keys(detailRecord.extraction).length > 0 && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">Extracted fields</p>
                  <pre className="mt-2 max-h-44 overflow-auto rounded-lg bg-muted p-3 text-xs">{JSON.stringify(detailRecord.extraction, null, 2)}</pre>
                </div>
              )}
              {canManage && detailRecord.status === 'awaiting_approval' && (
                <Field label="Message if details are missing">
                  <Textarea
                    value={requestNote}
                    onChange={(event) => setRequestNote(event.target.value)}
                    placeholder="Example: add final amount, vendor name, or clearer receipt photo"
                  />
                </Field>
              )}
              {canEditDetails(detailRecord) && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">{canManage ? 'Edit cost details' : 'Complete missing details'}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field label="Title"><Input value={detailDraft.title} onChange={(event) => setDetailDraft({ ...detailDraft, title: event.target.value })} /></Field>
                    <Field label="Amount"><Input type="number" min="0" step="0.01" value={detailDraft.amount} onChange={(event) => setDetailDraft({ ...detailDraft, amount: event.target.value })} /></Field>
                    <Field label="Category"><Select value={detailDraft.category_id} onChange={(value) => setDetailDraft({ ...detailDraft, category_id: value })} options={[['', 'Uncategorized'], ...(context?.categories || []).map((item) => [item.id, item.name])]} /></Field>
                    <div className="sm:col-span-2"><Field label="Notes"><Textarea value={detailDraft.description} onChange={(event) => setDetailDraft({ ...detailDraft, description: event.target.value })} /></Field></div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {canManage && <Button type="button" variant="outline" disabled={busy === 'details'} onClick={() => saveRecordDetails()}>Save details</Button>}
                    {!canManage && detailRecord.status === 'needs_information' && (
                      <Button type="button" disabled={busy === 'details'} onClick={() => saveRecordDetails({ submitForApproval: true })}>Resend for approval</Button>
                    )}
                  </div>
                </div>
              )}
              {payments.filter((item) => item.record_id === detailRecord.id).length > 0 && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">Payment history</p>
                  <div className="mt-3 space-y-2">
                    {payments.filter((item) => item.record_id === detailRecord.id).map((item) => (
                      <div key={item.id} className="flex items-center justify-between rounded-lg bg-muted/40 p-2 text-sm">
                        <span>{formatMoney(item.amount, currency)}</span>
                        <span className="text-muted-foreground">{item.method || 'Payment'} - {formatDate(item.paid_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2 sm:gap-2">
            {canManage && detailRecord && ['awaiting_approval', 'needs_information'].includes(detailRecord.status) && (
              <>
                {detailRecord.status === 'awaiting_approval' && <Button type="button" variant="outline" onClick={() => requestDetails(detailRecord, requestNote)}>Request details</Button>}
                <Button type="button" variant="outline" onClick={() => updateStatus(detailRecord, 'rejected')}>Reject</Button>
                {detailRecord.status === 'awaiting_approval' && <Button type="button" onClick={() => updateStatus(detailRecord, 'approved')}>Approve</Button>}
              </>
            )}
            {canManage && detailRecord && ['approved', 'scheduled'].includes(detailRecord.status) && detailRecord.outstanding_amount > MONEY_ZERO && (
              <>
                {Number(detailRecord.paid_amount || 0) > MONEY_ZERO && (
                  <Button type="button" variant="outline" onClick={() => declineRemaining(detailRecord)}>Decline rest</Button>
                )}
                <Button type="button" onClick={() => { setPaymentRecord(detailRecord); setDetailRecord(null); }}>Record payment</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(paymentRecord)} onOpenChange={(open) => !open && setPaymentRecord(null)}>
        <DialogContent>
          <form onSubmit={submitPayment}>
            <DialogHeader>
              <DialogTitle>Record payment</DialogTitle>
              <DialogDescription>{paymentRecord?.title} - outstanding {formatMoney(paymentRecord?.outstanding_amount, currency)}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-5 sm:grid-cols-2">
              <Field label="Amount paid"><Input required type="number" min="0.01" max={paymentRecord?.outstanding_amount} step="0.01" value={payment.amount} onChange={(event) => setPayment({ ...payment, amount: event.target.value })} /></Field>
              <Field label="Payment date">
                <EventAwareDatePicker value={payment.paid_at} onChange={(date) => setPayment({ ...payment, paid_at: date })} eventStartDate={currentEvent?.start_date} eventEndDate={currentEvent?.end_date} />
              </Field>
              <Field label="Method"><Input placeholder="Bank transfer, cash, UPI" value={payment.method} onChange={(event) => setPayment({ ...payment, method: event.target.value })} /></Field>
              <Field label="Reference"><Input value={payment.reference} onChange={(event) => setPayment({ ...payment, reference: event.target.value })} /></Field>
              <div className="sm:col-span-2"><Field label="Note"><Textarea value={payment.note} onChange={(event) => setPayment({ ...payment, note: event.target.value })} /></Field></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setPaymentRecord(null)}>Cancel</Button><Button type="submit" disabled={busy === 'payment'}>Record payment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{categoryEditing ? 'Edit category' : 'Add category'}</DialogTitle>
            <DialogDescription>Set the category budget and optional team auto-approval limit.</DialogDescription>
          </DialogHeader>
          {categoryEditing ? (
            <CategoryEditor category={categoryEditing} currency={currency} busy={busy === categoryEditing.id} onSave={saveCategory} onDelete={deleteCategory} />
          ) : (
            <div className="space-y-4 py-2">
              <Field label="Category name"><Input value={newCategory.name} onChange={(event) => setNewCategory({ ...newCategory, name: event.target.value })} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Planned amount"><Input type="number" min="0" value={newCategory.planned_amount} onChange={(event) => setNewCategory({ ...newCategory, planned_amount: event.target.value })} /></Field>
                <Field label="Budget limit"><Input type="number" min="0" value={newCategory.approved_amount} onChange={(event) => setNewCategory({ ...newCategory, approved_amount: event.target.value })} /></Field>
              </div>
              <Field label="Auto-approve team claims up to">
                <Input type="number" min="0" placeholder="Leave blank to require approval" value={newCategory.team_auto_approve_limit} onChange={(event) => setNewCategory({ ...newCategory, team_auto_approve_limit: event.target.value })} />
              </Field>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button><Button type="button" onClick={createCategory} disabled={busy === 'category-create'}>Add category</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Detail({ label, value }) {
  return (
    <div className="rounded-xl border bg-muted/30 p-3">
      <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium">{value}</p>
    </div>
  );
}
